import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, filter, of, take } from 'rxjs';
import { Routing } from '../../app.routes';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { LanguageStore } from '../../shared/services/language.store';
import { SnackBarService } from '../../shared/services/snack-bar.service';
import { StepperService } from '../../shared/services/stepper.service';
import {
  AuthEntity,
  AuthStateInterface,
  AuthStateService,
  PayloadModulrToken
} from '../../shared/services/state/auth-state.service';
import { SubscriptionFeatureService } from '../../shared/services/feature/subscription-feature.service';
import { ProjectStateService } from '../../shared/services/state/project-state.service';
import { IAcsHttpService } from '../../shared/services/http/acs/acs-http-service.interface';
import { ACS_HTTP_SERVICE } from '../../shared/services/http/http-service.token';
import { TokenResponse } from '../../shared/services/http/dto';

@Component({
  selector: 'app-acs-reprise-projet',
  imports: [
    CommonModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIcon,
    TranslatePipe
  ],
  templateUrl: './reprise-projet.component.html',
  styles: `
    @media only screen and (min-width: 1224px) {
      form {
        width: 418px;
        margin-left: 15%;
      }

      .full-height {
        height: 65vh;
      }
    }
  `,
  providers: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RepriseProjetComponent implements OnInit {
  public form: FormGroup = inject(FormBuilder).group({
    code: [
      null,
      [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern('[0-9]{6}')
      ]
    ]
  });
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly authStateService: AuthStateService = inject(AuthStateService);
  readonly #stepperService: StepperService = inject(StepperService);
  readonly #snackBarService: SnackBarService = inject(SnackBarService);
  readonly #translatePipe = inject(TranslatePipe);
  readonly #languageStore = inject(LanguageStore);
  readonly #router: Router = inject(Router);
  readonly #subscriptionFeatureService = inject(SubscriptionFeatureService);
  readonly #projectStateService = inject(ProjectStateService);
  readonly #acsHttpService: IAcsHttpService = inject(ACS_HTTP_SERVICE);
  private readonly langReady$ = toObservable(this.#languageStore.currentLanguage).pipe(
    filter(Boolean),
    take(1)
  );
  #uuid!: string;
  #producerCode!: string|null;
  #mode!: string|null;
  #entity!: string;
  public isLoading: WritableSignal<boolean> = signal(false);

  public toastMessage: Signal<string> = computed(() => {
    this.#languageStore.currentLanguageCode();
    return this.#translatePipe.transform('recovery.toast');
  });

  /**
   * A l init du composant on verifie que les parametre de l url sont bon
   * Puis on envoye une requete de code de reprise projet ( envoye par email )
   */
  public ngOnInit(): void {
    const params = this.#activatedRoute.snapshot.data[
      'queryParams'
    ] as AuthStateInterface;
    if (params.uuid && params.entity) {
      this.#uuid = params.uuid;
      this.#entity = params.entity;
      this.#producerCode = params.producerCode;
      this.#mode = params.mode;
      this.langReady$.subscribe(() => this.#snackBarService.success(this.toastMessage()));
    }
  }

  private get codeCtrl(): FormControl {
    return this.form.get('code') as FormControl;
  }

  /**
   * Soumission du formulaire pour l envoie du code recu par email
   * Set les parametre d'url et redirige vers la page projet
   */
  public confirmCode(): void {
    if (this.#uuid) {
      this.isLoading.set(true);
      this.authStateService
        .requestAccessToken(this.#uuid, this.codeCtrl.value)
        .pipe(
          catchError((err) => {
            this.isLoading.set(false);
            this.#snackBarService.errorMessage(err.error.message);
            return of(null);
          })
        )
        .subscribe((res) => {
          if (res) {
            // L'origine de l'affaire est lue depuis la claim signée du JWT retourné après
            // validation du code — jamais depuis une donnée fournie par le client (paramètre
            // d'URL, corps de requête), qui serait falsifiable (cf. MC-6785).
            const tokenPayload: PayloadModulrToken = jwtDecode(res.data.token);
            const businessOriginInternet =
              tokenPayload.business_origin_internet ?? null;

            this.authStateService.setToken(res.data.token);
            this.authStateService.updateData({
              token: res.data.token,
              uuid: this.#uuid,
              producerCode: this.#producerCode,
              mode: this.#mode,
              entity: this.#entity as AuthEntity,
              token_producer: res.data.token_producer,
              businessOriginInternet
            });
            this.#subscriptionFeatureService
              .getProjectInfo(this.#uuid, this.#producerCode, this.#mode)
              .pipe(
                catchError((err) => {
                  this.isLoading.set(false);
                  this.#snackBarService.errorMessage(err.error.message);
                  return of(null);
                })
              )
              .subscribe((project) => {
                if (project) {
                  // Un client qui reprend un projet créé lors d'un parcours apporteur (mode client +
                  // origine non-Internet) est envoyé directement à l'étape 4 (MC-6783/MC-6785) ; tout
                  // autre cas (apporteur, client grand public) conserve le comportement actuel.
                  const isClientOfProducerProject =
                    this.#mode === 'expat_external_client' &&
                    businessOriginInternet === false;

                  if (isClientOfProducerProject) {
                    // GET /project/{uuid} ne renvoie que les données de formulaire (pricing), pas
                    // le tarif calculé (periodicity_pricing) dont l'écran étape 4 a besoin — ce
                    // dernier n'est normalement calculé qu'en traversant l'étape 3 (Tarifs). Sauté
                    // ici, il faut le recalculer explicitement avant d'arriver sur l'écran.
                    this.#acsHttpService
                      .postExpatTarificationCalculate({
                        pricing: project.pricing,
                        beneficiaries: project.beneficiaries,
                        product: project.product
                      })
                      .pipe(
                        catchError((err) => {
                          this.#snackBarService.errorMessage(err.error.message);
                          return of(null);
                        })
                      )
                      .subscribe((pricingResult) => {
                        if (pricingResult) {
                          this.#projectStateService.setExpatPricing(pricingResult);
                        }
                        this.#navigateAfterCode(
                          pricingResult ? Routing.SOUSCRIPTION : Routing.PROJECT,
                          res.data
                        );
                      });
                  } else {
                    this.#navigateAfterCode(Routing.PROJECT, res.data);
                  }
                }
              });
          }
        });
    }
  }

  #navigateAfterCode(targetRoute: string, tokenData: TokenResponse): void {
    this.isLoading.set(false);
    // La route "souscription" est protégée par stepperGuard, qui exige que ce guardPath
    // (sessionStorage) corresponde à la route ciblée — sans quoi l'accès direct est refusé
    // (redirection vers "403").
    if (targetRoute === Routing.SOUSCRIPTION) {
      this.#projectStateService.setGuardPath(Routing.SOUSCRIPTION);
      // repriseProjectResolver masque le fil d'ariane le temps de la saisie du code : il faut le
      // réafficher en entrant dans le tunnel, l'étape 4 étant atteinte sans passer par l'écran
      // "projet" qui s'en charge habituellement. Il sera verrouillé pour ce client (MC-6785).
      this.#stepperService.isVisible.set(true);
    }

    this.#router
      .navigate([targetRoute], {
        queryParams: {
          token: tokenData.token,
          uuid: this.#uuid,
          producer_code: this.#producerCode,
          mode: this.#mode,
          entity: this.#entity,
          token_producer: tokenData.token_producer
        }
      })
      .then();
  }
}
